<script setup lang="ts">
import { computed, useId } from 'vue';
import { classNames } from '@/js/lib/bem';

const props = withDefaults(
  defineProps<{
    label?: string
    placeholder?: string
    name: string
    type?: string
    modelValue?: string
    autocomplete?: string
    disabled?: boolean
    invalid?: boolean
    message?: string
    required?: boolean
    staticLabel?: boolean
  }>(),
  {
    type: 'text',
    modelValue: '',
    autocomplete: undefined,
    disabled: false,
    invalid: false,
    message: undefined,
    staticLabel: false,
    placeholder: " "
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>();

const inputId = useId();

const cn = classNames('base-field');

const fieldClasses = computed(() =>
  cn({
    ...(props.invalid ? { state: 'invalid' } : {}),
    ...(props.staticLabel ? { label: 'static' } : {}),
  }),
);
</script>

<template>
  <div :class="[fieldClasses, cn('stacked')]">
    <div :class="cn('control')">
      <input
        :id="inputId"
        :name="name"
        :type="type"
        :value="modelValue"
        :autocomplete="autocomplete"
        :disabled="disabled"
        :required="required"
        :placeholder="placeholder"
        @input="
          emit('update:modelValue', ($event.target as HTMLInputElement).value)
        "
      >
      <label v-if="label" :for="inputId" class="base-field__label">{{ label }}</label>
    </div>
    <p v-if="message" :class="cn('message')" role="alert">
      {{ message }}
    </p>
  </div>
</template>

<style lang="scss" scoped>
.base-field {
  &__stacked {
    flex-direction: column;
  }

  &__control {
    position: relative;
  }

  &__message {
    margin: 4px 0 0 6px;
    color: var(--base-field-font-color);
    font-size: 12px;
    line-height: 1.3;
  }
}
</style>
